// pages/UserAdmin/crm/EscopoSelecao.js
//
// Faixa "Esta página / Toda a pasta" logo abaixo do cabeçalho da tabela de
// Contatos (out/26).
//
// A lista vem de 20 em 20 e o checkbox do cabeçalho marca a página. Até
// aqui era só isso: disparar template para uma pasta de 127 contatos eram 7
// rodadas de "marca, envia, próxima página" (Pontal). Quando a página
// inteira está marcada e o filtro tem mais contatos do que ela, esta faixa
// aparece com as duas opções lado a lado — a escolha fica visível o tempo
// todo, e a barra de ações de baixo age sobre o que estiver ativo aqui.
//
// O fio verde que sai do checkbox do cabeçalho e entra na faixa diz de onde
// ela veio: é o mesmo checkbox, com o alcance escolhido.
//
// "Toda a pasta" pede os ids ao servidor (GET /contacts/ids), com os
// mesmos filtros e a mesma ordem da lista. Quem decide quando a faixa
// aparece e o que está selecionado é a página; aqui é só a apresentação.

import { Box, CircularProgress, Typography } from '@mui/material';
import { CRM_THEME } from './theme';
import { Icon } from './icons';

// Out/26 (CRM moderno): cores do tema (claro/escuro). O segmento ativo é
// "tinta" — o texto da página como fundo —, que lê como escolhido nos dois
// temas sem disputar com o verde da faixa.
const VERDE = 'var(--ch-green)';

const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');

// `curto` é o rótulo do celular: em 320px "Esta página" e "Toda a pasta"
// saíam cortados ("Esta pá…", "Toda…"). A contagem ao lado já diz o tamanho
// de cada um, e a frase de baixo diz que vale pras outras páginas.
function Segmento({ ativo, onClick, carregando, rotulo, curto, contagem, icone, titulo }) {
  return (
    <Box
      component="button"
      type="button"
      role="radio"
      aria-checked={ativo}
      aria-busy={carregando || undefined}
      // Nome completo pro leitor de tela em qualquer largura (no celular o
      // texto visível é o curto).
      aria-label={`${rotulo}, ${fmt(contagem)} contato${contagem === 1 ? '' : 's'}`}
      title={titulo}
      onClick={ativo ? undefined : onClick}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.75,
        minWidth: 0, flex: { xs: 1, sm: '0 1 auto' }, justifyContent: 'center',
        px: 1.4, py: 0.55,
        border: 'none', borderRadius: '999px',
        bgcolor: ativo ? 'var(--ch-text)' : 'transparent',
        color: ativo ? 'var(--ch-card)' : 'var(--ch-text2)',
        fontFamily: CRM_THEME.font.ui, fontSize: 13.5, fontWeight: 600, height: 30,
        whiteSpace: 'nowrap',
        cursor: ativo ? 'default' : 'pointer',
        transition: 'background-color .15s, color .15s',
        '&:hover': ativo ? {} : { bgcolor: 'var(--ch-inset)', color: 'var(--ch-text)' },
        '&:focus-visible': { outline: `2px solid ${VERDE}`, outlineOffset: 2 },
      }}
    >
      {icone}
      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
        {rotulo}
      </Box>
      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
        {curto}
      </Box>
      {carregando ? (
        <CircularProgress size={11} thickness={6} sx={{ color: ativo ? 'var(--ch-card)' : VERDE, flexShrink: 0 }} />
      ) : (
        <Box component="span" sx={{
          fontFamily: CRM_THEME.font.num, fontSize: 12.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
          px: 1, py: '1px', borderRadius: '999px', flexShrink: 0,
          bgcolor: ativo ? 'rgba(127,127,127,.25)' : 'var(--ch-inset)',
          color: 'inherit',
        }}>
          {fmt(contagem)}
        </Box>
      )}
    </Box>
  );
}

/**
 * @param modo        'pagina' | 'tudo'
 * @param naPagina    quantos contatos a página atual mostra
 * @param total       quantos o filtro tem ao todo
 * @param selecionados quantos entraram no modo "tudo" (≤ teto do servidor)
 * @param escopo      { prefixo, nome?, cor? } — "Toda a pasta" + "Academias"
 * @param carregando  pedindo os ids ao servidor
 * @param erro        o pedido falhou
 */
export default function EscopoSelecao({
  modo, naPagina, total, selecionados, escopo, carregando, erro,
  colunas, onPagina, onTudo,
}) {
  const tudo = modo === 'tudo';
  const truncado = tudo && selecionados < total;

  return (
    <Box
      data-screen-label="Escopo da seleção"
      sx={{
        display: 'grid',
        ...colunas,
        alignItems: 'center',
        px: '20px', py: '9px',
        bgcolor: 'var(--ch-green-bg)',
        borderBottom: '1px solid var(--ch-green-b)',
        '@keyframes escopoEntra': {
          from: { opacity: 0, transform: 'translateY(-3px)' },
          to: { opacity: 1, transform: 'none' },
        },
        animation: 'escopoEntra .18s ease-out',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      {/* O fio: desce do checkbox do cabeçalho e dobra na altura do
          seletor — altura fixa, não 50%: no celular a frase quebra pra
          linha de baixo e o meio da faixa já não é o seletor. */}
      <Box aria-hidden sx={{ position: 'relative', alignSelf: 'stretch' }}>
        <Box sx={{
          // 9.25 + metade do traço = o meio da caixinha de 18px do cabeçalho.
          position: 'absolute', left: 9.25, top: -9,
          width: 10, height: 30,
          borderLeft: `1.5px solid ${VERDE}`, borderBottom: `1.5px solid ${VERDE}`,
          borderBottomLeftRadius: 7,
        }} />
      </Box>

      <Box sx={{
        gridColumn: '2 / -1', minWidth: 0,
        display: 'flex', alignItems: 'center', flexWrap: 'wrap',
        columnGap: 1.5, rowGap: 0.6, pl: 0.75,
      }}>
        <Typography component="span" sx={{
          display: { xs: 'none', sm: 'inline' },
          fontFamily: CRM_THEME.font.ui, fontSize: 14, fontWeight: 600,
          color: 'var(--ch-green-d)',
        }}>
          Selecionar
        </Typography>

        <Box
          role="radiogroup"
          aria-label="Alcance da seleção"
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: '2px',
            p: '3px', minWidth: 0, maxWidth: '100%',
            flex: { xs: '1 1 100%', sm: '0 1 auto' },
            bgcolor: 'var(--ch-card)', border: '1px solid var(--ch-green-b)', borderRadius: '999px',
          }}
        >
          <Segmento
            ativo={!tudo && !carregando}
            onClick={onPagina}
            rotulo="Esta página"
            curto="Página"
            contagem={naPagina}
            titulo="Só os contatos que aparecem nesta página"
          />
          <Segmento
            ativo={tudo}
            carregando={carregando}
            onClick={onTudo}
            contagem={total}
            titulo={escopo.nome ? `${escopo.prefixo} ${escopo.nome}` : escopo.prefixo}
            icone={escopo.nome ? (
              <Box component="span" sx={{ display: 'inline-flex', color: escopo.cor || 'var(--cx-cap)', flexShrink: 0 }}>
                <Icon.Folder size={12} />
              </Box>
            ) : null}
            rotulo={escopo.nome ? `${escopo.prefixo} ${escopo.nome}` : escopo.prefixo}
            curto={escopo.curto}
          />
        </Box>

        {erro ? (
          <Typography component="span" role="alert" sx={{ fontFamily: CRM_THEME.font.ui, fontSize: 13.5, color: 'var(--ch-red-d)', fontWeight: 600 }}>
            Não deu para selecionar todos. Tente de novo.
          </Typography>
        ) : tudo ? (
          <Typography component="span" aria-live="polite" sx={{ fontFamily: CRM_THEME.font.ui, fontSize: 13.5, color: 'var(--ch-green-d)', lineHeight: 1.35 }}>
            {truncado
              ? <>Entram os primeiros <b>{fmt(selecionados)}</b> de {fmt(total)}, na ordem da lista.</>
              : <>As ações valem para os <b>{fmt(selecionados)}</b>, inclusive os das outras páginas.</>}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}
